import React, { useEffect, useMemo, useRef, useState } from "react";
import { api } from "@/lib/api";
import { useCurrency } from "@/context/CurrencyContext";
import { useAuth } from "@/context/AuthContext";
import { PageHeader, SectionCard, Loading, EmptyState, Money } from "@/components/common/ui";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Badge } from "@/components/ui/badge";
import { Select, SelectTrigger, SelectValue, SelectContent, SelectItem } from "@/components/ui/select";
import { Tabs, TabsList, TabsTrigger, TabsContent } from "@/components/ui/tabs";
import { ToggleGroup, ToggleGroupItem } from "@/components/ui/toggle-group";
import { GovernedEditableField } from "@/components/GovernedEditableField";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription, DialogFooter,
} from "@/components/ui/dialog";
import {
  AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription,
  AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger,
} from "@/components/ui/alert-dialog";
import { Tooltip, TooltipTrigger, TooltipContent } from "@/components/ui/tooltip";
import { toast } from "sonner";
import { formatCount } from "@/lib/format";
import {
  Plus, Pencil, Trash2, Users, Eye, Home, Wallet, Info, ListChecks,
  Upload, Download, Lightbulb,
} from "lucide-react";
import { StrategySection } from "@/components/dashboard/StrategySection";

const DASH = "\u2014";
const CR = 10000000;
const QUARTERS = ["Q1", "Q2", "Q3", "Q4"];
const MONTHS = ["Apr-26", "May-26", "Jun-26", "Jul-26", "Aug-26", "Sep-26", "Oct-26", "Nov-26", "Dec-26", "Jan-27", "Feb-27", "Mar-27"];
const WEEKS = ["W1", "W2", "W3", "W4", "W5"];
const VERTICALS = ["ATL", "BTL", "Digital", "Brand", "CX"];
const FUNNEL_STAGES = ["Site Visit Push", "Lead Generation", "Conversion Support - Lead Gen", "Awareness", "Brand Visibility"];
const STAGE_ORDER = Object.fromEntries(FUNNEL_STAGES.map((s, i) => [s, i]));
const STAGE_ACCENT = {
  "Site Visit Push": "hsl(var(--chart-cat-3))",
  "Lead Generation": "hsl(var(--chart-cat-4))",
  "Conversion Support - Lead Gen": "hsl(var(--chart-cat-6))",
  "Awareness": "hsl(var(--chart-cat-1))",
  "Brand Visibility": "hsl(var(--chart-cat-2))",
};
const STATUSES = [
  { key: "planned", label: "Planned", cls: "border-[hsl(var(--info)/0.4)] text-[hsl(var(--info))]" },
  { key: "in-progress", label: "In Progress", cls: "border-[hsl(var(--warning)/0.4)] text-[hsl(var(--warning))]" },
  { key: "done", label: "Done", cls: "border-[hsl(var(--success)/0.4)] text-[hsl(var(--success))]" },
];
const STATUS_MAP = Object.fromEntries(STATUSES.map((s) => [s.key, s]));
const ALL = "__all__";
const NONE = "__none__";

const Q_MONTHS = {
  Q1: ["Apr-26", "May-26", "Jun-26"],
  Q2: ["Jul-26", "Aug-26", "Sep-26"],
  Q3: ["Oct-26", "Nov-26", "Dec-26"],
  Q4: ["Jan-27", "Feb-27", "Mar-27"],
};
const Q_OF = {};
Object.entries(Q_MONTHS).forEach(([q, ms]) => ms.forEach((m) => { Q_OF[m] = q; }));

const EMPTY = {
  project: "", vertical: "Digital", channel: "", title: "", funnel_stage: "Lead Generation", role: "",
  quarter: "Q2", month: "Jul-26", week: "", status: "planned",
  budget: 0, expected_leads: 0, expected_svs: 0, expected_bookings: 0, cost_per_sv: 0,
  spend_actual: 0, leads_actual: 0, sv_actual: 0, booking_actual: 0, owner: "", notes: "",
};

function downloadCsv(filename, rows) {
  const csv = rows.map((r) => r.map((c) => {
    const s = String(c ?? "");
    return /[",\n]/.test(s) ? `"${s.replace(/"/g, '""')}"` : s;
  }).join(",")).join("\n");
  const blob = new Blob([csv], { type: "text/csv;charset=utf-8;" });
  const url = URL.createObjectURL(blob);
  const a = document.createElement("a");
  a.href = url; a.download = filename; a.click();
  URL.revokeObjectURL(url);
}

/* Inline count editor */
function InlineNumber({ value, onSave, canEdit, testid, scale = 1, fmt }) {
  const [editing, setEditing] = useState(false);
  const disp = (value ?? 0) / scale;
  const [v, setV] = useState(disp);
  useEffect(() => { setV((value ?? 0) / scale); }, [value, scale]);
  const show = fmt ? fmt(value ?? 0) : ((value ?? 0) ? formatCount(value) : DASH);
  if (!canEdit) return <span className="tabular-nums">{show}</span>;
  if (!editing) {
    return (
      <button type="button" onClick={() => setEditing(true)} data-testid={testid}
        className="group -mx-1 inline-flex items-center gap-1 rounded px-1 py-0.5 hover:bg-[hsl(var(--primary)/0.08)] focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-[hsl(var(--ring))]">
        <span className="tabular-nums font-medium">{show}</span>
        <Pencil className="h-3 w-3 opacity-0 transition-opacity group-hover:opacity-60" />
      </button>
    );
  }
  const commit = () => { setEditing(false); const nv = (Number(v) || 0) * scale; if (nv !== (value ?? 0)) onSave(nv); };
  return (
    <Input autoFocus type="number" min="0" value={v} onChange={(e) => setV(e.target.value)}
      onBlur={commit}
      onKeyDown={(e) => { if (e.key === "Enter") commit(); if (e.key === "Escape") { setV(disp); setEditing(false); } }}
      className="h-7 w-[72px] px-1.5 text-right text-xs tabular-nums" data-testid={`${testid}-input`} />
  );
}

/* Upload + template toolbar (per tab) */
function UploadBar({ onUpload, onTemplate, uploading, testPrefix, accept = ".xlsx,.csv" }) {
  const fileRef = useRef(null);
  return (
    <div className="flex items-center gap-2">
      <input ref={fileRef} type="file" accept={accept} className="hidden" data-testid={`${testPrefix}-file-input`}
        onChange={(e) => { const f = e.target.files?.[0]; if (f) onUpload(f); if (fileRef.current) fileRef.current.value = ""; }} />
      <Button variant="outline" size="sm" className="h-9 gap-1.5" onClick={onTemplate} data-testid={`${testPrefix}-template`}>
        <Download className="h-4 w-4" /> Template
      </Button>
      <Button variant="outline" size="sm" className="h-9 gap-1.5" disabled={uploading} onClick={() => fileRef.current?.click()} data-testid={`${testPrefix}-upload`}>
        <Upload className="h-4 w-4" /> {uploading ? "Uploading\u2026" : "Upload"}
      </Button>
    </div>
  );
}

/* ============================ Plan Activities ============================ */
function ActivitiesView({ pmode, quarter, month }) {
  const { money } = useCurrency();
  const { user, isFull, isManager, assignedVerticals } = useAuth();
  const canEdit = isFull || isManager;
  // Vertical leads may only pick their assigned vertical(s) when adding/editing.
  const myVerticals = (assignedVerticals && assignedVerticals.length) ? assignedVerticals : VERTICALS;
  const vertOptions = isManager ? myVerticals : VERTICALS;
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [projects, setProjects] = useState([]);
  const [filters, setFilters] = useState({ project: ALL, vertical: ALL, stage: ALL, status: ALL });
  const [groupBy, setGroupBy] = useState("project");
  const [uploading, setUploading] = useState(false);

  const [dlgOpen, setDlgOpen] = useState(false);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);

  const load = () => {
    setLoading(true);
    api.planActivities().then((d) => {
      setData(d.activities || []);
      setProjects(d.projects || []);
      setLoading(false);
    }).catch(() => setLoading(false));
  };
  useEffect(() => { load(); }, []);

  const filtered = useMemo(() => {
    if (!data) return [];
    return data.filter((a) =>
      (pmode === "Quarter" ? a.quarter === quarter : a.month === month) &&
      (filters.project === ALL || a.project === filters.project) &&
      (filters.vertical === ALL || a.vertical === filters.vertical) &&
      (filters.stage === ALL || a.funnel_stage === filters.stage) &&
      (filters.status === ALL || a.status === filters.status));
  }, [data, filters, pmode, quarter, month]);

  const totals = useMemo(() => {
    const sum = (f) => filtered.reduce((s, a) => s + (Number(a[f]) || 0), 0);
    return {
      count: filtered.length,
      budget: sum("budget"), spend: sum("spend_actual"),
      exp_leads: sum("expected_leads"), act_leads: sum("leads_actual"),
      exp_svs: sum("expected_svs"), act_svs: sum("sv_actual"),
      act_bookings: sum("booking_actual"),
    };
  }, [filtered]);

  const groups = useMemo(() => {
    const keyOf = (a) => groupBy === "project" ? (a.project || "Unassigned")
      : groupBy === "vertical" ? (a.vertical || "Unassigned")
        : groupBy === "stage" ? (a.funnel_stage || "Unassigned") : (a.month || "Unscheduled");
    const map = {};
    filtered.forEach((a) => { (map[keyOf(a)] = map[keyOf(a)] || []).push(a); });
    const entries = Object.entries(map);
    entries.forEach(([, rows]) => rows.sort((x, y) =>
      (STAGE_ORDER[x.funnel_stage] ?? 99) - (STAGE_ORDER[y.funnel_stage] ?? 99)));
    return entries.sort((a, b) => a[0].localeCompare(b[0]));
  }, [filtered, groupBy]);

  const periodDefaults = () => ({
    quarter: pmode === "Quarter" ? quarter : Q_OF[month],
    month: pmode === "Month" ? month : "",
  });
  const openAdd = () => { setEditing(null); setForm({ ...EMPTY, ...periodDefaults(), owner: user?.name || "", ...(isManager ? { vertical: myVerticals[0] } : {}) }); setDlgOpen(true); };
  const openEdit = (a) => { setEditing(a); setForm({ ...EMPTY, ...a, owner: a.owner || user?.name || "" }); setDlgOpen(true); };
  const setF = (patch) => setForm((f) => ({ ...f, ...patch }));

  const buildBody = (obj, extra = {}) => ({
    project: obj.project, vertical: obj.vertical, channel: obj.channel, title: obj.title,
    funnel_stage: obj.funnel_stage, role: obj.role, quarter: obj.quarter, month: obj.month,
    week: obj.week, status: obj.status,
    budget: Number(obj.budget) || 0, expected_leads: Number(obj.expected_leads) || 0,
    expected_svs: Number(obj.expected_svs) || 0, expected_bookings: Number(obj.expected_bookings) || 0,
    cost_per_sv: Number(obj.cost_per_sv) || 0, spend_actual: Number(obj.spend_actual) || 0,
    leads_actual: Number(obj.leads_actual) || 0, sv_actual: Number(obj.sv_actual) || 0,
    booking_actual: Number(obj.booking_actual) || 0, owner: obj.owner, notes: obj.notes,
    ...extra,
  });

  const save = async () => {
    if (!form.project) { toast.error("Select a project"); return; }
    if (!form.channel.trim() && !form.title.trim()) { toast.error("Add a channel or activity title"); return; }
    setSaving(true);
    const body = buildBody(form, { reason: editing ? "plan activity update" : "plan activity create" });
    try {
      if (editing) await api.updatePlanActivity(editing.id, body);
      else await api.createPlanActivity(body);
      toast.success(editing ? "Activity updated" : "Activity added");
      setDlgOpen(false); load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Save failed"); }
    finally { setSaving(false); }
  };

  const remove = async (a) => {
    try { await api.deletePlanActivity(a.id); toast.success("Activity removed"); load(); }
    catch (e) { toast.error(e?.response?.data?.detail || "Delete failed"); }
  };

  const patchActual = (row, field) => async (value) => {
    const body = buildBody({ ...row, [field]: value }, { reason: "actual outcome update" });
    setData((d) => d.map((x) => x.id === row.id ? { ...x, [field]: value } : x));
    try { await api.updatePlanActivity(row.id, body); }
    catch (e) { toast.error("Update failed"); load(); }
  };

  const doUpload = async (file) => {
    setUploading(true);
    try {
      const fd = new FormData(); fd.append("file", file);
      const params = pmode === "Month" ? { quarter: Q_OF[month], month } : { quarter };
      const res = await api.uploadPlanActivities(fd, params);
      toast.success(`Imported ${res.inserted} activit${res.inserted === 1 ? "y" : "ies"}${res.errors?.length ? ` \u00b7 ${res.errors.length} skipped` : ""}`);
      if (res.errors?.length) toast.warning(res.errors.slice(0, 3).join("; "));
      load();
    } catch (e) { toast.error(e?.response?.data?.detail || "Upload failed"); }
    finally { setUploading(false); }
  };

  const downloadTemplate = () => {
    const headers = ["Project", "Vertical", "Channel", "Funnel Stage", "Quarter", "Month", "Budget", "Expected Leads", "Expected SV", "Cost/SV", "Actual Spend", "Actual Leads", "Actual SV", "Actual Bookings", "Status", "Owner", "Notes"];
    const example = ["Edition", "Digital", "Meta (Lead Form)", "Lead Generation", "Q2", "Jul-26", "500000", "1200", "300", "1500", "0", "0", "0", "0", "planned", "", ""];
    downloadCsv("plan-activities-template.csv", [headers, example]);
  };

  if (loading && !data) return <Loading />;
  const inr = (v) => (v ? `\u20b9${formatCount(v)}` : DASH);

  return (
    <div className="space-y-5">
      <div className="flex flex-wrap items-center justify-end gap-2">
        {isFull && <UploadBar onUpload={doUpload} onTemplate={downloadTemplate} uploading={uploading} testPrefix="plan-activities" />}
        {canEdit && (
          <Button onClick={openAdd} data-testid="plan-add-activity"><Plus className="mr-1.5 h-4 w-4" /> Add Activity</Button>
        )}
      </div>

      <div className="grid grid-cols-2 gap-3 lg:grid-cols-4" data-testid="plan-summary-strip">
        <PlanActualCard icon={Users} label="Leads" accent="hsl(var(--chart-cat-4))" expected={totals.exp_leads} actual={totals.act_leads} fmt={formatCount} testid="plan-summary-leads" />
        <PlanActualCard icon={Eye} label="Site Visits" accent="hsl(var(--chart-cat-3))" expected={totals.exp_svs} actual={totals.act_svs} fmt={formatCount} testid="plan-summary-svs" />
        <PlanActualCard icon={Home} label="Bookings" accent="hsl(var(--success))" actualOnly actual={totals.act_bookings} fmt={formatCount} testid="plan-summary-bookings" />
        <PlanActualCard icon={Wallet} label="Budget" accent="hsl(var(--chart-cat-6))" expected={totals.budget} actual={totals.spend} fmt={money} testid="plan-summary-budget" moneyMode />
      </div>

      <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-3" data-testid="plan-filters">
        <FilterSelect label="Project" value={filters.project} onChange={(v) => setFilters((f) => ({ ...f, project: v }))} options={projects} testid="plan-filter-project" />
        <FilterSelect label="Vertical" value={filters.vertical} onChange={(v) => setFilters((f) => ({ ...f, vertical: v }))} options={vertOptions} testid="plan-filter-vertical" />
        <FilterSelect label="Funnel" value={filters.stage} onChange={(v) => setFilters((f) => ({ ...f, stage: v }))} options={FUNNEL_STAGES} testid="plan-filter-stage" wide />
        <FilterSelect label="Status" value={filters.status} onChange={(v) => setFilters((f) => ({ ...f, status: v }))} options={STATUSES.map((s) => s.key)} labels={Object.fromEntries(STATUSES.map((s) => [s.key, s.label]))} testid="plan-filter-status" />
        <div className="ml-auto flex items-center gap-1.5">
          <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">Group by</span>
          <Select value={groupBy} onValueChange={setGroupBy}>
            <SelectTrigger className="h-8 w-[130px]" data-testid="plan-groupby"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="project">Project</SelectItem>
              <SelectItem value="stage">Funnel Stage</SelectItem>
              <SelectItem value="vertical">Vertical</SelectItem>
              <SelectItem value="month">Month</SelectItem>
            </SelectContent>
          </Select>
        </div>
      </div>

      {filtered.length === 0 ? (
        <EmptyState title="No activities" message={canEdit ? `No planned activities for ${pmode === "Quarter" ? quarter : month}. Add one${isFull ? " or upload a plan" : ""} for this period.` : "No planned activities for this filter."} />
      ) : (
        <div className="space-y-4">
          {groups.map(([g, rows]) => {
            const gExpLeads = rows.reduce((s, a) => s + (a.expected_leads || 0), 0);
            const gExpSv = rows.reduce((s, a) => s + (a.expected_svs || 0), 0);
            const gBudget = rows.reduce((s, a) => s + (a.budget || 0), 0);
            return (
              <SectionCard key={g} title={g}
                description={`${rows.length} activit${rows.length === 1 ? "y" : "ies"} \u00b7 ${formatCount(gExpLeads)} exp. leads \u00b7 ${formatCount(gExpSv)} exp. SV \u00b7 ${money(gBudget)} planned`}
                testId={`plan-group-${g.replace(/[^a-zA-Z0-9]+/g, "-")}`}>
                <div className="table-scroll">
                  <table className="w-full border-collapse text-sm">
                    <thead>
                      <tr className="border-b">
                        <th className="th-sticky th-sticky-left px-3 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Activity</th>
                        <th className="th-sticky px-2.5 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Stage</th>
                        <th className="th-sticky border-l px-2.5 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Exp. Leads</th>
                        <th className="th-sticky px-2.5 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--chart-cat-4))]">Act. Leads</th>
                        <th className="th-sticky border-l px-2.5 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Exp. SV</th>
                        <th className="th-sticky px-2.5 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--chart-cat-3))]">Act. SV</th>
                        <th className="th-sticky border-l px-2.5 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Plan ₹</th>
                        <th className="th-sticky px-2.5 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-[hsl(var(--chart-cat-6))]">Act. Spend</th>
                        <th className="th-sticky px-2.5 py-2 text-right text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Cost/SV</th>
                        <th className="th-sticky px-2.5 py-2 text-left text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Status</th>
                        <th className="th-sticky px-2 py-2" />
                      </tr>
                    </thead>
                    <tbody>
                      {rows.map((a, i) => (
                        <tr key={a.id} data-testid={`plan-row-${a.id}`} className={`border-b last:border-0 ${i % 2 ? "row-zebra" : ""}`}>
                          <td className="td-sticky-left px-3 py-2">
                            <div className="flex items-center gap-1.5">
                              <span className="font-medium">{a.channel || a.title || "Untitled"}</span>
                              {a.role && (
                                <Tooltip>
                                  <TooltipTrigger asChild>
                                    <button type="button" className="text-muted-foreground hover:text-foreground" data-testid={`plan-role-${a.id}`} aria-label="Role in plan">
                                      <Info className="h-3.5 w-3.5" />
                                    </button>
                                  </TooltipTrigger>
                                  <TooltipContent className="max-w-[280px] text-xs">{a.role}</TooltipContent>
                                </Tooltip>
                              )}
                            </div>
                            {groupBy !== "project" && <span className="text-[11px] text-muted-foreground">{a.project}</span>}
                          </td>
                          <td className="px-2.5 py-2">
                            <Badge variant="outline" className="text-[9px]" style={{ borderColor: `${STAGE_ACCENT[a.funnel_stage] || "hsl(var(--border))"}66`, color: STAGE_ACCENT[a.funnel_stage] }}>
                              {a.funnel_stage || DASH}
                            </Badge>
                          </td>
                          <td className="border-l px-2.5 py-2 text-right tabular-nums text-muted-foreground">{a.expected_leads ? formatCount(a.expected_leads) : DASH}</td>
                          <td className="px-2.5 py-2 text-right"><InlineNumber value={a.leads_actual} canEdit={canEdit} onSave={patchActual(a, "leads_actual")} testid={`plan-actual-leads-${a.id}`} /></td>
                          <td className="border-l px-2.5 py-2 text-right tabular-nums text-muted-foreground">{a.expected_svs ? formatCount(a.expected_svs) : DASH}</td>
                          <td className="px-2.5 py-2 text-right"><InlineNumber value={a.sv_actual} canEdit={canEdit} onSave={patchActual(a, "sv_actual")} testid={`plan-actual-sv-${a.id}`} /></td>
                          <td className="border-l px-2.5 py-2 text-right tabular-nums text-muted-foreground"><Money value={a.budget || 0} /></td>
                          <td className="px-2.5 py-2 text-right tabular-nums font-medium"><Money value={a.spend_actual || 0} /></td>
                          <td className="px-2.5 py-2 text-right tabular-nums text-xs text-muted-foreground">{inr(a.cost_per_sv)}</td>
                          <td className="px-2.5 py-2">
                            <Badge variant="outline" className={"text-[9px] " + (STATUS_MAP[a.status]?.cls || "")}>{STATUS_MAP[a.status]?.label || a.status}</Badge>
                          </td>
                          <td className="px-2 py-2 text-right">
                            {canEdit && (
                              <div className="flex items-center justify-end gap-0.5">
                                <Button size="icon" variant="ghost" className="h-7 w-7" onClick={() => openEdit(a)} data-testid={`plan-edit-${a.id}`}><Pencil className="h-3.5 w-3.5" /></Button>
                                <AlertDialog>
                                  <AlertDialogTrigger asChild>
                                    <Button size="icon" variant="ghost" className="h-7 w-7 text-muted-foreground hover:text-[hsl(var(--danger))]" data-testid={`plan-delete-${a.id}`}><Trash2 className="h-3.5 w-3.5" /></Button>
                                  </AlertDialogTrigger>
                                  <AlertDialogContent>
                                    <AlertDialogHeader><AlertDialogTitle>Remove activity?</AlertDialogTitle>
                                      <AlertDialogDescription>“{a.channel || a.title}” will be permanently removed from the plan.</AlertDialogDescription></AlertDialogHeader>
                                    <AlertDialogFooter>
                                      <AlertDialogCancel>Cancel</AlertDialogCancel>
                                      <AlertDialogAction onClick={() => remove(a)} data-testid={`plan-delete-confirm-${a.id}`}>Remove</AlertDialogAction>
                                    </AlertDialogFooter>
                                  </AlertDialogContent>
                                </AlertDialog>
                              </div>
                            )}
                          </td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </SectionCard>
            );
          })}
        </div>
      )}

      <Dialog open={dlgOpen} onOpenChange={setDlgOpen}>
        <DialogContent className="max-h-[90vh] overflow-y-auto sm:max-w-[680px]" data-testid="plan-activity-dialog">
          <DialogHeader>
            <DialogTitle>{editing ? "Edit activity" : "Add activity"}</DialogTitle>
            <DialogDescription>Plan a campaign activity and track its outcome — expected vs actual.</DialogDescription>
          </DialogHeader>

          <div className="grid grid-cols-2 gap-3">
            <Field label="Project">
              <Select value={form.project || undefined} onValueChange={(v) => setF({ project: v })}>
                <SelectTrigger data-testid="plan-form-project"><SelectValue placeholder="Select project" /></SelectTrigger>
                <SelectContent>{projects.map((p) => <SelectItem key={p} value={p}>{p}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Vertical">
              <Select value={form.vertical} onValueChange={(v) => setF({ vertical: v })} disabled={isManager && myVerticals.length === 1}>
                <SelectTrigger data-testid="plan-form-vertical"><SelectValue /></SelectTrigger>
                <SelectContent>{vertOptions.map((v) => <SelectItem key={v} value={v}>{v}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Channel / Activity" full>
              <Input value={form.channel} onChange={(e) => setF({ channel: e.target.value })} placeholder="e.g. Meta (Lead Form), Revspot (SV)" data-testid="plan-form-channel" />
            </Field>
            <Field label="Funnel stage">
              <Select value={form.funnel_stage} onValueChange={(v) => setF({ funnel_stage: v })}>
                <SelectTrigger data-testid="plan-form-stage"><SelectValue /></SelectTrigger>
                <SelectContent>{FUNNEL_STAGES.map((s) => <SelectItem key={s} value={s}>{s}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Status">
              <Select value={form.status} onValueChange={(v) => setF({ status: v })}>
                <SelectTrigger data-testid="plan-form-status"><SelectValue /></SelectTrigger>
                <SelectContent>{STATUSES.map((s) => <SelectItem key={s.key} value={s.key}>{s.label}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Quarter">
              <Select value={form.quarter} onValueChange={(v) => setF({ quarter: v })}>
                <SelectTrigger data-testid="plan-form-quarter"><SelectValue /></SelectTrigger>
                <SelectContent>{QUARTERS.map((q) => <SelectItem key={q} value={q}>{q}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Month">
              <Select value={form.month || NONE} onValueChange={(v) => setF({ month: v === NONE ? "" : v })}>
                <SelectTrigger data-testid="plan-form-month"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>— none —</SelectItem>{MONTHS.map((m) => <SelectItem key={m} value={m}>{m}</SelectItem>)}</SelectContent>
              </Select>
            </Field>
            <Field label="Week">
              <Select value={form.week || NONE} onValueChange={(v) => setF({ week: v === NONE ? "" : v })}>
                <SelectTrigger data-testid="plan-form-week"><SelectValue /></SelectTrigger>
                <SelectContent><SelectItem value={NONE}>— none —</SelectItem>{WEEKS.map((w) => <SelectItem key={w} value={w}>{w}</SelectItem>)}</SelectContent>
              </Select>
            </Field>

            <div className="col-span-2 mt-1 rounded-lg border bg-[hsl(var(--surface-2))] p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Plan (Expected)</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Field label="Budget (₹)"><Input type="number" min="0" value={form.budget} onChange={(e) => setF({ budget: e.target.value })} data-testid="plan-form-budget" /></Field>
                <Field label="Exp. Leads"><Input type="number" min="0" value={form.expected_leads} onChange={(e) => setF({ expected_leads: e.target.value })} data-testid="plan-form-exp-leads" /></Field>
                <Field label="Exp. Site Visits"><Input type="number" min="0" value={form.expected_svs} onChange={(e) => setF({ expected_svs: e.target.value })} data-testid="plan-form-exp-svs" /></Field>
                <Field label="Cost / SV (₹)"><Input type="number" min="0" value={form.cost_per_sv} onChange={(e) => setF({ cost_per_sv: e.target.value })} data-testid="plan-form-cost-sv" /></Field>
              </div>
            </div>

            <div className="col-span-2 rounded-lg border bg-[hsl(var(--surface-2))] p-3">
              <p className="mb-2 text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Actual (Outcome)</p>
              <div className="grid grid-cols-2 gap-3 sm:grid-cols-4">
                <Field label="Act. Spend (₹)"><Input type="number" min="0" value={form.spend_actual} onChange={(e) => setF({ spend_actual: e.target.value })} data-testid="plan-form-act-spend" /></Field>
                <Field label="Act. Leads"><Input type="number" min="0" value={form.leads_actual} onChange={(e) => setF({ leads_actual: e.target.value })} data-testid="plan-form-act-leads" /></Field>
                <Field label="Act. Site Visits"><Input type="number" min="0" value={form.sv_actual} onChange={(e) => setF({ sv_actual: e.target.value })} data-testid="plan-form-act-svs" /></Field>
                <Field label="Act. Bookings"><Input type="number" min="0" value={form.booking_actual} onChange={(e) => setF({ booking_actual: e.target.value })} data-testid="plan-form-act-bookings" /></Field>
              </div>
            </div>

            <Field label="Role in plan" full>
              <Textarea value={form.role} onChange={(e) => setF({ role: e.target.value })} className="min-h-[52px]" placeholder="Why this activity is in the plan…" data-testid="plan-form-role" />
            </Field>
            <Field label="Owner"><Input value={form.owner} readOnly disabled className="cursor-not-allowed bg-[hsl(var(--surface-2))]" title="Auto-filled from your account" data-testid="plan-form-owner" /></Field>
            <Field label="Notes"><Input value={form.notes} onChange={(e) => setF({ notes: e.target.value })} placeholder="Optional notes" data-testid="plan-form-notes" /></Field>
          </div>

          <DialogFooter>
            <Button variant="outline" onClick={() => setDlgOpen(false)}>Cancel</Button>
            <Button onClick={save} disabled={saving} data-testid="plan-form-save">{saving ? "Saving…" : editing ? "Save changes" : "Add activity"}</Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>
    </div>
  );
}

/* ===================== Strategy & Governance (merged tab) ===================== */
function StrategyView() {
  const { money } = useCurrency();
  const { isFull } = useAuth();
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    let on = true; setLoading(true);
    api.marketingHead("FY", [])
      .then((d) => { if (on) { setData(d); setLoading(false); } })
      .catch(() => { if (on) setLoading(false); });
    return () => { on = false; };
  }, []);

  if (loading && !data) return <Loading />;
  if (!data) return <EmptyState title="No data" message="Could not load the strategy view." />;
  return <StrategySection strategy={data.strategy} groups={data.project_groups} money={money} canEdit={isFull} />;
}

/* ============================ Shell ============================ */
function PlanPeriodBar({ pmode, setPmode, quarter, setQuarter, month, setMonth }) {
  return (
    <div className="flex flex-wrap items-center gap-2 rounded-lg border bg-card p-2.5" data-testid="plan-period-bar">
      <span className="text-[11px] font-semibold uppercase tracking-wide text-muted-foreground">Period</span>
      <ToggleGroup type="single" value={pmode} onValueChange={(v) => v && setPmode(v)} className="rounded-lg border bg-card p-0.5" data-testid="plan-period-mode">
        <ToggleGroupItem value="Quarter" className="h-7 px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" data-testid="plan-period-mode-quarter">Quarter</ToggleGroupItem>
        <ToggleGroupItem value="Month" className="h-7 px-2.5 text-xs data-[state=on]:bg-primary data-[state=on]:text-primary-foreground" data-testid="plan-period-mode-month">Month</ToggleGroupItem>
      </ToggleGroup>
      {pmode === "Quarter" ? (
        <Select value={quarter} onValueChange={setQuarter}>
          <SelectTrigger className="h-8 w-[110px]" data-testid="plan-period-quarter-select"><SelectValue /></SelectTrigger>
          <SelectContent>{QUARTERS.map((q) => <SelectItem key={q} value={q} data-testid={`plan-period-q-${q}`}>{q}</SelectItem>)}</SelectContent>
        </Select>
      ) : (
        <Select value={month} onValueChange={setMonth}>
          <SelectTrigger className="h-8 w-[128px]" data-testid="plan-period-month-select"><SelectValue /></SelectTrigger>
          <SelectContent className="max-h-[320px]">{MONTHS.map((m) => <SelectItem key={m} value={m} data-testid={`plan-period-m-${m}`}>{m}</SelectItem>)}</SelectContent>
        </Select>
      )}
      <span className="ml-1 text-[11px] text-muted-foreground">Filters both tabs</span>
    </div>
  );
}

export default function MarketingPlan() {
  const { isManager, isFull } = useAuth();
  const [pmode, setPmode] = useState("Quarter");
  const [quarter, setQuarter] = useState("Q2");
  const [month, setMonth] = useState("Jul-26");
  // Vertical leads only see Strategy & Governance if an admin enables it centrally.
  const [showStrategy, setShowStrategy] = useState(!isManager);
  useEffect(() => {
    if (!isManager) { setShowStrategy(true); return; }
    let on = true;
    api.leadAccess()
      .then((cfg) => { if (on) setShowStrategy(!!cfg?.show_strategy); })
      .catch(() => { if (on) setShowStrategy(false); });
    return () => { on = false; };
  }, [isManager]);

  const [tab, setTab] = useState(() => {
    try {
      const t = new URLSearchParams(window.location.search).get("tab");
      return t === "strategy" || t === "activities" ? t : "activities";
    } catch { return "activities"; }
  });
  // If a lead lands on the strategy tab but it's disabled, snap back to Monthly Plan.
  useEffect(() => {
    if (!showStrategy && tab === "strategy") setTab("activities");
  }, [showStrategy, tab]);

  return (
    <div className="space-y-5" data-testid="marketing-plan-page">
      <PageHeader title="Marketing Plan"
        description="The monthly activity plan that drives the numbers, plus leadership strategy & governance. Targets vs Achieved now lives in the Targets module." />
      {tab !== "strategy" && (
        <PlanPeriodBar pmode={pmode} setPmode={setPmode} quarter={quarter} setQuarter={setQuarter} month={month} setMonth={setMonth} />
      )}
      <Tabs value={tab} onValueChange={setTab} className="w-full">
        <TabsList data-testid="marketing-plan-tabs">
          <TabsTrigger value="activities" data-testid="tab-plan-activities"><ListChecks className="mr-1.5 h-4 w-4" /> Monthly Plan</TabsTrigger>
          {showStrategy && (
            <TabsTrigger value="strategy" data-testid="tab-plan-strategy"><Lightbulb className="mr-1.5 h-4 w-4" /> Strategy &amp; Governance</TabsTrigger>
          )}
        </TabsList>
        <TabsContent value="activities" className="mt-4"><ActivitiesView pmode={pmode} quarter={quarter} month={month} /></TabsContent>
        {showStrategy && (
          <TabsContent value="strategy" className="mt-4"><StrategyView /></TabsContent>
        )}
      </Tabs>
    </div>
  );
}

function PlanActualCard({ icon: Icon, label, expected, actual, fmt, accent, testid, actualOnly, moneyMode, targetMode }) {
  const att = expected ? Math.round((actual / expected) * 100) : null;
  return (
    <div className="rounded-xl border bg-card p-3.5 card-shadow" data-testid={testid}>
      <div className="flex items-center justify-between">
        <span className="flex h-8 w-8 items-center justify-center rounded-lg" style={{ background: `${accent}1f`, color: accent }}><Icon className="h-4 w-4" /></span>
        {att != null && (
          <Badge variant="outline" className="text-[10px] tabular-nums" data-testid={`${testid}-attainment`}>{att}%</Badge>
        )}
      </div>
      <p className="mt-2.5 text-[11px] font-medium text-muted-foreground">{label}</p>
      <p className="mt-0.5 font-heading text-lg font-semibold tabular-nums" style={{ color: accent }} data-testid={`${testid}-actual`}>
        {fmt(actual || 0)}
      </p>
      {!actualOnly && (
        <p className="mt-0.5 text-[11px] text-muted-foreground" data-testid={`${testid}-expected`}>
          {moneyMode ? "Planned" : targetMode ? "Target" : "Target"} <span className="tabular-nums text-foreground">{fmt(expected || 0)}</span>
        </p>
      )}
      {actualOnly && <p className="mt-0.5 text-[11px] text-muted-foreground">Actual bookings logged</p>}
    </div>
  );
}

function FilterSelect({ label, value, onChange, options, labels, testid, wide }) {
  return (
    <div className="flex items-center gap-1.5">
      <span className="text-[11px] font-medium uppercase tracking-wide text-muted-foreground">{label}</span>
      <Select value={value} onValueChange={onChange}>
        <SelectTrigger className={`h-8 ${wide ? "w-[190px]" : "w-[150px]"}`} data-testid={testid}><SelectValue /></SelectTrigger>
        <SelectContent>
          <SelectItem value={ALL}>All</SelectItem>
          {options.map((o) => <SelectItem key={o} value={o}>{labels ? labels[o] : o}</SelectItem>)}
        </SelectContent>
      </Select>
    </div>
  );
}

function Field({ label, children, full }) {
  return (
    <div className={full ? "col-span-2" : ""}>
      <label className="mb-1 block text-[11px] font-medium text-muted-foreground">{label}</label>
      {children}
    </div>
  );
}
